import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  EIremboFormlyFieldTypes,
  EIremboFormlyFieldSubTypes,
  EIremboFormlyFormStateKeys,
} from '@irembo-andela/irembogov3-common';
import { GetEnumToLabelAndValueOptions } from '../../form-builder-field-comparator-dropdown-options.utils';
import {
  EFormBuilderDisplayComparatorActions,
  EFormBuilderDisplayConditionComparator,
  EFormBuilderDisplayFieldComparator,
  FormBuilderFieldsWithDataSet,
  FormBuilderFieldsWithOptions,
} from '../../../models/form-builder-display-comparator.enum';
import { TFormlyFieldKey } from '../../../models/formly-key.type';
import {
  IBasicFieldLabelAndDetails,
  IBasicLabelValuePair,
} from '../../../models/basic-label-value-pair.model';
import { FormBuilderService } from '../../../services/form-builder.service';
import { EDisplayRuleComparedAgainst } from '../../../models/form-fields-formly-display.model';

export const FETCHED_DATA_FIELD_TYPES: EIremboFormlyFieldTypes[] = [
  EIremboFormlyFieldTypes.customapplicationnumberinput,
  EIremboFormlyFieldTypes.customcascadingeditableinput,
  EIremboFormlyFieldTypes.customcrvsidinput,
  EIremboFormlyFieldTypes.customdoubleidinput,
  EIremboFormlyFieldTypes.customeditableinput,
  EIremboFormlyFieldTypes.genericbuttonfetch,
  EIremboFormlyFieldTypes.customgenericdatafetch,
  EIremboFormlyFieldTypes.customidinput,
  EIremboFormlyFieldTypes.customotpdatafetch,
  EIremboFormlyFieldTypes.customtininput,
];

export const getFieldDisplayFormConfig = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig[] => {
  const configuredFieldLabelAndDetails =
    formBuilderServiceInstance.getCurrentConfiguredFieldLabelAndDetails();
  const fetchedDataFieldsFormFieldLabelAndDetails: IBasicLabelValuePair<string>[] =
    [];

  const otherFormFieldLabelAndDetails: IBasicLabelValuePair<
    IBasicFieldLabelAndDetails<TFormlyFieldKey>
  >[] = [];

  formBuilderServiceInstance
    .getAllFormFieldLabelAndDetails(true, true, [
      EIremboFormlyFieldTypes.customcascadingdropdowns,
    ])
    .forEach((fieldDetails: IBasicFieldLabelAndDetails<TFormlyFieldKey>) => {
      if (
        FETCHED_DATA_FIELD_TYPES.includes(
          <EIremboFormlyFieldTypes>fieldDetails.fieldType
        )
      ) {
        fetchedDataFieldsFormFieldLabelAndDetails.push({
          label: fieldDetails.label,
          value: fieldDetails?.fieldKey ?? '',
        });
      }

      otherFormFieldLabelAndDetails.push({
        label: fieldDetails.label,
        value: fieldDetails,
      });
    });

  return [
    {
      key: 'configuredFieldRootModelPath',
      type: EIremboFormlyFieldTypes.input,
      className: 'form-builder-display-configured-field-root-model-path',
      defaultValue: configuredFieldLabelAndDetails?.rootModelPath,
      props: {
        readonly: true,
        label: 'Configured Field Root Model Path',
        hideField: true,
      },
    },
    {
      key: 'configuredFieldRefPath',
      type: EIremboFormlyFieldTypes.input,
      className: 'form-builder-display-configured-field-ref-path',
      defaultValue: configuredFieldLabelAndDetails?.fieldRefPath,
      props: {
        readonly: true,
        label: 'Configured Field Ref Path',
        hideField: true,
      },
    },
    {
      key: 'action',
      type: EIremboFormlyFieldTypes.customdropdown,
      className: 'form-builder-display-action-selector',
      props: {
        label: 'This field when',
        placeholder: 'Show or Hide',
        required: true,
        options: GetEnumToLabelAndValueOptions(
          EFormBuilderDisplayComparatorActions
        ),
        bindLabel: 'label',
        bindValue: 'value',
      },
    },
    {
      key: 'displayRules',
      type: EIremboFormlyFieldTypes.customrepeater,
      className: 'form-builder-display-rules',
      props: {
        hideHeader: true,
        required: false,
        addText: 'Add Condition',
        title: 'Condition',
        template: 'displayRules',
      },
      fieldArray: {
        key: 'displayRulesGroup',
        className: 'form-builder-display-rules-group',
        type: EIremboFormlyFieldTypes['formly-group'],
        props: {
          hideHeader: true,
          placeholder: 'Display List',
          required: false,
        },
        fieldGroup: [
          {
            key: 'comparator',
            type: EIremboFormlyFieldTypes.customdropdown,
            props: {
              hideHeader: true,
              placeholder: 'AND or OR',
              required: false,
              options: GetEnumToLabelAndValueOptions(
                EFormBuilderDisplayConditionComparator
              ),
              bindLabel: 'label',
              bindValue: 'value',
            },
            expressions: {
              'props.required': (field: FormlyFieldConfig) => {
                return field?.parent?.key !== '0';
              },
              hide: (field: FormlyFieldConfig) => {
                return field?.parent?.key === '0';
              },
            },
          },
          {
            key: 'comparedAgainst',
            type: EIremboFormlyFieldTypes.customdropdown,
            defaultValue: EDisplayRuleComparedAgainst.fieldValue,
            props: {
              hideHeader: true,
              placeholder: 'Compared to',
              required: true,
              bindValue: 'value',
              bindLabel: 'label',
              options: [
                {
                  label: 'Form Field',
                  value: EDisplayRuleComparedAgainst.fieldValue,
                },
                {
                  label: 'Form State',
                  value: EDisplayRuleComparedAgainst.formStateValue,
                },
                {
                  label: 'Form Field Validity',
                  value: EDisplayRuleComparedAgainst.fieldValidity,
                },
              ],
            },
          },
          {
            key: 'comparedFormStateValuePath',
            type: EIremboFormlyFieldTypes['formly-group'],
            props: {
              hideHeader: true,
              placeholder: 'Display List',
              required: false,
            },
            fieldGroup: [
              {
                key: 'formStateKey',
                type: EIremboFormlyFieldTypes.select,
                props: {
                  hideHeader: true,
                  placeholder: 'Select form State Key',
                  required: true,
                  bindValue: 'value',
                  options: [
                    {
                      label: 'Fetched Data',
                      value: EIremboFormlyFormStateKeys.FETCHED_DATA,
                    },
                    {
                      label: 'Application State',
                      value: EIremboFormlyFormStateKeys.APPLICATION_STATE,
                    },
                    {
                      label: 'Applicant Type',
                      value: EIremboFormlyFormStateKeys.APPLICANT_TYPE,
                    },
                    {
                      label: 'Application Locale',
                      value: EIremboFormlyFormStateKeys.LOCALE,
                    },
                  ],
                },
                expressions: {
                  'props.description': (field: FormlyFieldConfig) => {
                    let description: string | undefined = undefined;
                    switch (field?.formControl?.value) {
                      case EIremboFormlyFormStateKeys.APPLICANT_BUSINESS_ID: {
                        description =
                          'Stores the business ID for business applicants';
                        break;
                      }
                      case EIremboFormlyFormStateKeys.APPLICANT_BUSINESS_NAME: {
                        description =
                          'The business name for business applicants';
                        break;
                      }
                      case EIremboFormlyFormStateKeys.APPLICANT_TYPE: {
                        description = 'The applicant type';
                        break;
                      }
                      case EIremboFormlyFormStateKeys.APPLICATION_STATE: {
                        description = 'The application state';
                        break;
                      }
                      case EIremboFormlyFormStateKeys.FETCHED_DATA: {
                        description =
                          'Fetched data received by fields that fetch external data against thier field key';
                        break;
                      }
                      case EIremboFormlyFormStateKeys.FETCHED_OPTIONS_DATA: {
                        description =
                          'Fetched list of fetched options received by custom dropdowns with data fetch';
                        break;
                      }
                      case EIremboFormlyFormStateKeys.LOCALE: {
                        description = 'The locale used in the application';
                        break;
                      }
                      default:
                        description = '';
                    }
                    return description;
                  },
                },
              },
              {
                key: 'fieldKey',
                type: EIremboFormlyFieldTypes.customdropdown,
                props: {
                  hideHeader: true,
                  placeholder: 'Select the field key in the formState data',
                  required: true,
                  bindLabel: 'label',
                  bindValue: 'value',
                  options: fetchedDataFieldsFormFieldLabelAndDetails,
                },
                expressions: {
                  hide: `!(model?.formStateKey === '${EIremboFormlyFormStateKeys.FETCHED_DATA}')`,
                },
              },
              {
                key: 'valuePath',
                type: EIremboFormlyFieldTypes.input,
                props: {
                  hideHeader: true,
                  placeholder: 'Value path',
                  required: true,
                  description:
                    'value path to access value e.g `sex` in {sex: "MALE"} or `users?.[0].sex` in  {users: [sex: "MALE"]}',
                },
              },
            ],
            expressions: {
              hide: (field: FormlyFieldConfig) =>
                field.parent?.model?.comparedAgainst !==
                EDisplayRuleComparedAgainst.formStateValue,
            },
          },
          {
            key: 'compareField',
            type: EIremboFormlyFieldTypes.select,
            props: {
              hideHeader: true,
              placeholder: 'Select Field',
              required: true,
              compareWith: (
                o1: IBasicLabelValuePair<TFormlyFieldKey>,
                o2: IBasicLabelValuePair<TFormlyFieldKey>
              ) => {
                return o1?.value === o2?.value;
              },
              options: otherFormFieldLabelAndDetails,
            },
            expressions: {
              hide: (field: FormlyFieldConfig) =>
                field.parent?.model?.comparedAgainst !==
                  EDisplayRuleComparedAgainst.fieldValue &&
                field.parent?.model?.comparedAgainst !==
                  EDisplayRuleComparedAgainst.fieldValidity,
            },
          },
          {
            key: 'compareFieldType',
            type: EIremboFormlyFieldTypes.input,
            hide: true,
            props: {
              hideHeader: true,
              required: true,
            },
            expressions: {
              'model.compareFieldType': (field: FormlyFieldConfig) =>
                field.parent?.model?.compareField?.fieldType,
            },
          },
          {
            key: 'compareFieldPropsType',
            type: EIremboFormlyFieldTypes.input,
            hide: true,
            props: {
              hideHeader: true,
              required: true,
            },
            expressions: {
              'model.compareFieldPropsType': (field: FormlyFieldConfig) =>
                field.parent?.model?.compareField?.propsType,
            },
          },
          {
            key: 'condition',
            type: EIremboFormlyFieldTypes.select,
            props: {
              hideHeader: true,
              placeholder: 'select condition',
              required: true,
              options: GetEnumToLabelAndValueOptions(
                EFormBuilderDisplayFieldComparator
              ),
            },
            expressions: {
              'props.options': (field: FormlyFieldConfig) => {
                const options: IBasicLabelValuePair<string>[] =
                  GetEnumToLabelAndValueOptions(
                    EFormBuilderDisplayFieldComparator
                  );
                if (
                  field.parent?.model?.comparedAgainst ===
                  EDisplayRuleComparedAgainst.formStateValue
                ) {
                  return options;
                }
                if (
                  field.parent?.model?.comparedAgainst ===
                    EDisplayRuleComparedAgainst.fieldValidity ||
                  field.parent?.model?.compareField?.fieldType == 'checkbox'
                ) {
                  return options.filter(
                    x =>
                      x.value ===
                      EFormBuilderDisplayFieldComparator.IS_BOOLEAN_TRUE
                  );
                }
                const compareFieldType: EIremboFormlyFieldTypes =
                  field.parent?.model?.compareField?.fieldType;
                if (!compareFieldType) {
                  return;
                }
                if (
                  FormBuilderFieldsWithOptions.includes(compareFieldType) ||
                  FormBuilderFieldsWithDataSet.includes(compareFieldType) ||
                  [
                    EIremboFormlyFieldTypes.customidinput,
                    EIremboFormlyFieldTypes.customphonenumber,
                    EIremboFormlyFieldTypes.textarea,
                  ].includes(compareFieldType)
                ) {
                  const allowedOptions: IBasicLabelValuePair<string>[] = [];
                  options.forEach(option => {
                    if (
                      [
                        EFormBuilderDisplayFieldComparator.IS_EQUAL_TO,
                        EFormBuilderDisplayFieldComparator.IS_NOT_EQUAL_TO,
                      ].includes(
                        option.value as EFormBuilderDisplayFieldComparator
                      )
                    ) {
                      allowedOptions.push(option);
                    }
                  });
                  return allowedOptions;
                } else if (compareFieldType === EIremboFormlyFieldTypes.input) {
                  const allowedOptions: IBasicLabelValuePair<string>[] = [];
                  options.forEach(option => {
                    if (
                      [
                        EFormBuilderDisplayFieldComparator.IS_EQUAL_TO,
                        EFormBuilderDisplayFieldComparator.IS_NOT_EQUAL_TO,
                        EFormBuilderDisplayFieldComparator.IS_ABOVE,
                        EFormBuilderDisplayFieldComparator.IS_BELOW,
                      ].includes(
                        option.value as EFormBuilderDisplayFieldComparator
                      )
                    ) {
                      allowedOptions.push(option);
                    }
                  });
                  return allowedOptions;
                } else {
                  return options;
                }
              },
              'model.condition': 'model?.condition',
              hide: (field: FormlyFieldConfig) => {
                return (
                  field.parent?.model?.comparedAgainst ===
                  EDisplayRuleComparedAgainst.fieldValidity
                );
              },
            },
          },
          {
            key: 'comparedValues',
            type: EIremboFormlyFieldTypes['formly-group'],
            props: {
              hideHeader: true,
              placeholder: 'Compared Value',
            },
            fieldGroup: [
              {
                key: 'isValue',
                type: EIremboFormlyFieldTypes.input,
                props: {
                  hideHeader: true,
                  placeholder: 'Compared Value',
                  required: true,
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) =>
                    field.parent?.parent?.model?.comparedAgainst ===
                      EDisplayRuleComparedAgainst.fieldValidity ||
                    field?.model?.valueAsNumber ||
                    field.parent?.parent?.model?.condition ===
                      EFormBuilderDisplayFieldComparator.IS_BOOLEAN_TRUE ||
                    FormBuilderFieldsWithOptions.includes(
                      field.parent?.parent?.model?.compareField?.fieldType
                    ) ||
                    FormBuilderFieldsWithDataSet.includes(
                      field.parent?.parent?.model?.compareField?.fieldType
                    ),
                },
              },
              {
                key: 'isValue',
                type: EIremboFormlyFieldTypes.input,
                props: {
                  hideHeader: true,
                  placeholder: 'Compared Value (Number value)',
                  type: 'number',
                  required: true,
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) =>
                    !field?.model?.valueAsNumber ||
                    field.parent?.parent?.model?.condition ===
                      EFormBuilderDisplayFieldComparator.IS_BOOLEAN_TRUE ||
                    FormBuilderFieldsWithOptions.includes(
                      field.parent?.parent?.model?.compareField?.fieldType
                    ) ||
                    FormBuilderFieldsWithDataSet.includes(
                      field.parent?.parent?.model?.compareField?.fieldType
                    ),
                },
              },
              {
                key: 'valueAsNumber',
                type: EIremboFormlyFieldTypes.checkbox,
                defaultValue: false,
                props: {
                  hideHeader: true,
                  label: 'Value as Number',
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) =>
                    field.parent?.parent?.model?.condition ===
                      EFormBuilderDisplayFieldComparator.IS_BOOLEAN_TRUE ||
                    FormBuilderFieldsWithOptions.includes(
                      field.parent?.parent?.model?.compareField?.fieldType
                    ) ||
                    FormBuilderFieldsWithDataSet.includes(
                      field.parent?.parent?.model?.compareField?.fieldType
                    ) ||
                    field.parent?.parent?.model?.comparedAgainst ===
                      EDisplayRuleComparedAgainst.fieldValidity,
                },
              },
              {
                key: 'isValue',
                type: EIremboFormlyFieldTypes.customdropdownpaginated,
                props: {
                  hideHeader: true,
                  placeholder: 'Select',
                  options: [],
                  useBaseUrl: true,
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) =>
                    field.parent?.parent?.model?.condition ===
                      EFormBuilderDisplayFieldComparator.IS_BOOLEAN_TRUE ||
                    !FormBuilderFieldsWithDataSet.includes(
                      field.parent?.parent?.model?.compareField?.fieldType
                    ) ||
                    FormBuilderFieldsWithOptions.includes(
                      field.parent?.parent?.model?.compareField?.fieldType
                    ) ||
                    field.parent?.parent?.model?.compareField?.subType ===
                      EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH,
                  'props.required': (field: FormlyFieldConfig) =>
                    checkForInputWithOptionsAllowedComparators(
                      field.parent?.parent?.model?.condition
                    ),
                  'props.dataset': (field: FormlyFieldConfig) => {
                    if (
                      field.parent?.parent?.model?.compareField?.fieldType ===
                      EIremboFormlyFieldTypes.customdropdownpaginated
                    ) {
                      return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                        field.parent?.parent?.model
                      )?.props?.['dataset'];
                    }
                  },
                  'props.bindLabel': (field: FormlyFieldConfig) => {
                    if (
                      field.parent?.parent?.model?.compareField?.fieldType ===
                      EIremboFormlyFieldTypes.customdropdownpaginated
                    ) {
                      return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                        field.parent?.parent?.model
                      )?.props?.['bindLabel'];
                    }
                  },
                  'props.bindValue': (field: FormlyFieldConfig) => {
                    return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                      field.parent?.parent?.model
                    )?.props?.['bindValue'];
                  },
                },
              },
              {
                key: 'isValue',
                type: EIremboFormlyFieldTypes.select,
                props: {
                  hideHeader: true,
                  placeholder: 'Select',
                  options: [],
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) =>
                    !(
                      FormBuilderFieldsWithOptions.includes(
                        field.parent?.parent?.model?.compareField?.fieldType
                      ) &&
                      field.parent?.parent?.model?.compareField
                        ?.fieldSubType !==
                        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH &&
                      field.parent?.parent?.model?.compareField
                        ?.fieldSubType !==
                        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
                    ) ||
                    field.parent?.parent?.model?.condition ===
                      EFormBuilderDisplayFieldComparator.IS_BOOLEAN_TRUE ||
                    FormBuilderFieldsWithDataSet.includes(
                      field.parent?.parent?.model?.compareField?.fieldType
                    ),
                  'props.required': (field: FormlyFieldConfig) =>
                    checkForInputWithOptionsAllowedComparators(
                      field.parent?.parent?.model?.condition
                    ),
                  'props.options': (field: FormlyFieldConfig) => {
                    return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                      field.parent?.parent?.model
                    )?.props?.options;
                  },
                },
              },
              {
                key: 'isValue',
                type: EIremboFormlyFieldTypes.customdropdown,
                props: {
                  hideHeader: true,
                  placeholder: 'Select',
                  options: [],
                  useBaseUrl: true,
                  subType: EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH,
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) =>
                    field.parent?.parent?.model?.compareField?.fieldSubType !==
                    EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH,
                  'props.required': (field: FormlyFieldConfig) =>
                    checkForInputWithOptionsAllowedComparators(
                      field.parent?.parent?.model?.condition
                    ),
                  'props.dataset': (field: FormlyFieldConfig) => {
                    if (
                      field.parent?.parent?.model?.compareField
                        ?.fieldSubType ===
                      EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH
                    ) {
                      return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                        field.parent?.parent?.model
                      )?.props?.['dataset'];
                    }
                  },
                  'props.bindLabel': (field: FormlyFieldConfig) => {
                    return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                      field.parent?.parent?.model
                    )?.props?.['bindLabel'];
                  },
                  'props.bindValue': (field: FormlyFieldConfig) => {
                    return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                      field.parent?.parent?.model
                    )?.props?.['bindValue'];
                  },
                  'props.code': (field: FormlyFieldConfig) => {
                    return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                      field.parent?.parent?.model
                    )?.props?.['code'];
                  },
                },
              },
              {
                key: 'isValue',
                type: EIremboFormlyFieldTypes.customdropdown,
                props: {
                  hideHeader: true,
                  placeholder: 'Select',
                  options: [],
                  useBaseUrl: true,
                  subType: EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) =>
                    field.parent?.parent?.model?.condition ===
                      EFormBuilderDisplayFieldComparator.IS_BOOLEAN_TRUE ||
                    field.parent?.parent?.model?.compareField?.fieldSubType !==
                      EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
                  'props.required': (field: FormlyFieldConfig) =>
                    checkForInputWithOptionsAllowedComparators(
                      field.parent?.parent?.model?.condition
                    ),
                  'props.dataset': (field: FormlyFieldConfig) => {
                    if (
                      field.parent?.parent?.model?.compareField
                        ?.fieldSubType ===
                      EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
                    ) {
                      return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                        field.parent?.parent?.model
                      )?.props?.['dataset'];
                    }
                  },
                  'props.bindLabel': (field: FormlyFieldConfig) => {
                    return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                      field.parent?.parent?.model
                    )?.props?.['bindLabel'];
                  },
                  'props.bindValue': (field: FormlyFieldConfig) => {
                    return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                      field.parent?.parent?.model
                    )?.props?.['bindValue'];
                  },
                },
              },
              {
                key: 'isChecked',
                type: EIremboFormlyFieldTypes.customdropdown,
                props: {
                  hideHeader: true,
                  placeholder: 'Select boolean value',
                  required: true,
                  options: [
                    {
                      value: true,
                      label: 'True',
                    },
                    {
                      value: false,
                      label: 'False',
                    },
                  ],
                  multiple: false,
                  bindValue: 'value',
                  bindLabel: 'label',
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) =>
                    field.parent?.parent?.model?.condition !==
                    EFormBuilderDisplayFieldComparator.IS_BOOLEAN_TRUE,
                },
              },
              {
                key: 'validity',
                type: EIremboFormlyFieldTypes.customdropdown,
                props: {
                  hideHeader: true,
                  placeholder: 'Select Valid or Not Valid',
                  required: true,
                  options: [
                    {
                      value: true,
                      label: 'Is Valid',
                    },
                    {
                      value: false,
                      label: 'Not Valid',
                    },
                  ],
                  multiple: false,
                  bindValue: 'value',
                  bindLabel: 'label',
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) =>
                    field.parent?.parent?.model?.comparedAgainst !==
                    EDisplayRuleComparedAgainst.fieldValidity,
                },
              },
            ],
          },
        ],
      },
    },
  ];
};

export const checkForInputWithOptionsAllowedComparators = (
  value: string
): boolean => {
  const allowedOptions: string[] = [
    EFormBuilderDisplayFieldComparator.IS_EQUAL_TO,
    EFormBuilderDisplayFieldComparator.IS_NOT_EQUAL_TO,
  ];
  return allowedOptions.indexOf(value) > -1;
};
